import { AxiosResponse } from 'axios'

import { keyBy, omit } from '@src/utils/functions/utils'

import isBrowser from '../../utils/isBrowser'
import {
  CART_AUTH_CODE_KEY,
  CART_AUTH_CODE_RES_KEY,
  LOCAL_STORAGE_CART_OBJ_KEY,
  LOCAL_STORAGE_NEW_CART_ID_KEY,
} from '../checkoutService/offshopify.constants'
import { getInventory } from '../inventoryService/inventory.service'

import { getProductData } from './cart.service'

export function setCartAuthCodeFromHeader(res: AxiosResponse) {
  const cartAuthCode = res.headers[CART_AUTH_CODE_RES_KEY]
  if (cartAuthCode) {
    localStorage.setItem(CART_AUTH_CODE_KEY, cartAuthCode)
  }
}

export function getCartAuthCodeFromHeader() {
  if (!isBrowser()) return null

  const code = localStorage.getItem(CART_AUTH_CODE_KEY)
  return code
}

export async function getInventoryDataForCart(data: any) {
  if (data.lineItems?.length === 0 && (!data.saveForLaterItems?.length || data.saveForLaterItems.length === 0))
    return {}

  const variantIds = data.lineItems?.map((item: any) => item.variantId) || []
  const saveForLaterVariantIds = data.saveForLaterItems?.map((item: any) => item.variantId) || []

  const allVariantIds = [...variantIds, ...saveForLaterVariantIds].join(',')
  const inventoryData = await getInventory({ variantIds: allVariantIds })
  return inventoryData
}

export async function getProductDataForCart(data: any) {
  if (!isBrowser()) return {}
  const productIdMap =
    data?.lineItems?.reduce((acc: any, lineItem: any) => {
      return {
        ...acc,
        [lineItem.variantId]: lineItem?.variantMetaData?.productId,
      }
    }, {}) || {}

  const saveForLaterProductIdMap =
    data?.saveForLaterItems?.reduce((acc: any, lineItem: any) => {
      return {
        ...acc,
        [lineItem.variantId]: lineItem?.variantMetaData?.productId,
      }
    }, {}) || {}

  const totalProductIdMap = { ...productIdMap, ...saveForLaterProductIdMap }

  const productIds = Object.values(totalProductIdMap)
  const products =
    productIds.length > 0
      ? await getProductData({
          productIds,
        })
      : {}
  const productMap = keyBy(products, 'id')

  const productDataWithVariantMap = Object.entries(totalProductIdMap).reduce(
    (acc: any, [variantId, productId]: any) => {
      return {
        ...acc,
        [variantId]: {
          productData: omit(productMap[productId], ['variants']),
          selectedVariant: productMap[productId]?.variants?.find((variant: any) => {
            return variant?.id?.toString() === variantId
          }),
        },
      }
    },
    {},
  )
  return productDataWithVariantMap
}

export function mergeInventoryDataInCart(data: any, inventoryData: any) {
  const lineItems = data.lineItems.map((item: any) => {
    return {
      ...item,
      inventoryData: inventoryData[item.variantId],
    }
  })

  return {
    ...data,
    lineItems,
  }
}

function mergeProductDataInCart(data: any, _productData: any) {
  const lineItems = data.lineItems.map((lineItem: any) => {
    const variantId = lineItem.variantId
    const product = _productData[variantId]
    const productMetaData = product.selectedVariant
    const slug = product.productData.slug

    return {
      ...lineItem,
      productMetaData,
      slug,
    }
  })

  const saveForLaterlineItems =
    data?.saveForLaterItems?.map((lineItem: any) => {
      const variantId = lineItem.variantId
      const product = _productData[variantId]
      const productMetaData = product?.selectedVariant
      const slug = product?.productData?.slug

      return {
        ...lineItem,
        productMetaData,
        slug,
      }
    }) || []

  return { ...data, lineItems: lineItems, saveForLaterItems: saveForLaterlineItems }
}

export async function getDataWithProductAndInventory(data: any) {
  try {
    const [productData, inventoryData] = await Promise.all([getProductDataForCart(data), getInventoryDataForCart(data)])
    const inventoryDataWithCart = mergeInventoryDataInCart(data, inventoryData)
    const mergedData = mergeProductDataInCart(inventoryDataWithCart, productData)
    return mergedData
  } catch (error) {
    console.error(error)
    return data
  }
}

export const removeCartIdFromLocalStorage = () => {
  window.localStorage.removeItem(LOCAL_STORAGE_NEW_CART_ID_KEY)
  window.localStorage.removeItem(LOCAL_STORAGE_CART_OBJ_KEY)
}

export function setCartAuthCodeToLocalStorage(code: string) {
  localStorage.setItem(CART_AUTH_CODE_KEY, code)
}
